import { SectionHeading } from "@themes/pollinator/ui/SectionHeading";
import { Loader } from "@themes/pollinator/ui/Loader";
import { Type } from "@themes/pollinator/ui/Type";
import {
  useFetchOrgInsights,
  useFetchProgramInsightsTopline,
} from "./hooks/useFetchProgramInsights";
import { OrgInsights } from "./OrgInsights";

export type Props = {
  /** Program ID — drives which static JSON tree we fetch from. */
  programId: number | null;
  /** Organization ID — drives which per-org file we fetch. */
  organizationId: number;
};

/**
 * Self-contained "Sending Institution Insights" section for the org admin
 * tracking page. Wraps its own SectionHeading so it can render nothing
 * when no insights are published for the program — there's no half-empty
 * section heading dangling on the page.
 *
 * Renders:
 *  - null when there's no published topline for this program (no probe
 *    rerun has happened yet, or the program is too new). The whole
 *    section disappears.
 *  - The full `<OrgInsights>` view when the topline is present —
 *    delegating the org-vs-program-only choice to the presentational
 *    component based on whether the per-org file exists.
 */
export function OrgInsightsContainer({ programId, organizationId }: Props) {
  const { data: topline, isLoading: isToplineLoading } =
    useFetchProgramInsightsTopline(programId);
  const { data: org, isLoading: isOrgLoading } = useFetchOrgInsights(
    programId,
    organizationId
  );

  // No program insights published at all → hide the section entirely.
  // Loading still renders the section header so the page layout doesn't
  // jump as the data loads in.
  if (!isToplineLoading && topline == null) {
    return null;
  }

  return (
    <SectionHeading title="Sending Institution Insights">
      {isToplineLoading || isOrgLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : topline ? (
        <OrgInsights orgInsights={org ?? null} topline={topline} />
      ) : (
        <Type variant="body2" className="text-brand-700">
          Insights for this program are not available yet.
        </Type>
      )}
    </SectionHeading>
  );
}

export default OrgInsightsContainer;
